import { Moment } from "moment";
import Utils from "../../core/shared/utils";
import { SyncModel, SyncDataModel, DataModel } from "../../core/shared/models/syncModel";
import { ContentType, ImageOrientation } from "../../core/shared/contentTypes";
import { MenuSettings_SortType } from "../../core/shared/models/menu.model";
import { ArticleOptions } from "../../core/shared/models/webshop.model";

// ViewModel
export class SOTSettingsViewModel {

  public get allow_in_house(): boolean {
    return this._model.allow_in_house;
  }
  public get allow_pickup(): boolean {
    return this._model.allow_pickup;
  }
  public get skip_ordertype_if_no_choise(): boolean {
    return this._model.skip_ordertype_if_no_choise;
  }

  public get isProductAlphabeticSort(): boolean {
    return this._model.sort_type == MenuSettings_SortType.Alphabetic;
  }

  public get has_logo_image(): boolean {
    return this._model.last_logo_image_update_at_ymd > 0
  }
  public get logo_image_path(): string {
    if (this.has_logo_image)
      return ContentType.SOTLogoImage + "/" + this._model.location + "_logo_" + ImageOrientation.Portrait + "/" + this._model.last_logo_image_update_at_ymd + "/" + this._model.last_logo_image_update_at_hms;
    else
      return "";
  }
  public get has_payment_image(): boolean {
    return this._model.last_payment_image_update_at_ymd > 0
  }
  public get payment_image_path(): string {
    if (this.has_payment_image)
      return ContentType.SOTPaymentImage + "/" + this._model.location + "_photo_" + ImageOrientation.Landscape + "/" + this._model.last_payment_image_update_at_ymd + "/" + this._model.last_payment_image_update_at_hms;
    else
      return "";
  }

  public get use_product_availability(): boolean {
    return this._model.use_product_availability;
  }
  public get use_co2(): boolean {
    return this._model.use_co2;
  }
  public get use_nutrition_values(): boolean {
    return this._model.use_nutrition_values;
  }
  public get use_allergens(): boolean {
    return this._model.use_allergens;
  }
  public get use_additives(): boolean {
    return this._model.use_additives;
  }
  public get use_eCommerce(): boolean {
    return this._model.use_eCommerce;
  }
  public get languages(): string[] {
    return this._model.languages;
  }
  public get use_weehlchair_mode(): boolean {
    return false;
  }

  /** ermittelt aus der strache den sprachen namen
   * @param language - sprachkürzel (z.B. de, en, fr) aus languages
   */
  public getLanguageName(language: string): string {
    const language_index = this.model.languages.findIndex((lang) => lang == language);
    if (language_index > -1) {
      if (this._model.languages_name) {
        return this._model.languages_name[language_index];
      } else {
        return null; // languages_name nicht vorhanden
      }
    } else {
      return null; // kein Name vorhanden
    }
  }
  /** ermittelt anhand der language die URL zur Flagge
   * @param language_code - sprachkürzel (z.B. de, en, fr) aus languages
   */
  public getLanguageFlagUrl(language_code: string): string {
    const language_index = this.model.languages.findIndex((lang) => lang == language_code);
    if (language_index > -1) {
      return ContentType.Language + "/" + language_code + "_flag_" + ImageOrientation.Landscape + "/" + this.model.change_at_ymd + "/" + this.model.change_at_hms;
    } else {
      return null; // Sprache unbekannt
    }
  }
  public get currencySymbol(): string {
    return this._model.currencySymbol;
  }
  public get extra_as_image(): boolean {
    return this._model.extra_as_image;
  }

  public get request_tip(): boolean {
    return (this._model.request_tip != null) ? this._model.request_tip : true;
  }

  public get use_pager(): boolean {
    return (this._model.use_pager != null) ? this._model.use_pager : true;
  }

  public get use_coupons(): boolean {
    return (this._model.use_coupons != null) ? this._model.use_coupons : false;
  }

  public get scan_coupons(): boolean {
    return (this._model.scan_coupons != null) ? this._model.scan_coupons : false;
  }

  public get use_itemgroup_as_image(): boolean {
    return (this._model.use_itemgroup_as_image != null) ? this._model.use_itemgroup_as_image : true;
  }

  public get coupons_length(): number {
    return (this._model.coupons_length != null) ? this._model.coupons_length : 4;
  }

  public get allow_benefits_at_sot(): boolean {
    return (this._model.allow_benefits_at_sot != null) ? this._model.allow_benefits_at_sot : false;
  }

  /** deposit add to view price */
  public get deposit_view(): boolean {
    return this._model.deposit_view;
  }

  public get article_option(): ArticleOptions {
    return (this._model.article_option != null) ? this._model.article_option : ArticleOptions.hide;
  }

  private _model: SOTSettingsDataModel;
  public get model(): SOTSettingsDataModel {
    return this._model;
  }

  constructor(model: SOTSettingsDataModel) {
    const me = this;
    me._model = model;
  }
}

// Requests
export class SOTSettingsSyncModel extends SyncModel {
  public hyp_cus_nr: number;
}

// Responses
/** Representiert die einstellung eines SOTs */
export class SOTSettingsDataModel extends DataModel {
  public get DM_Key(): number | string { return this.id; }
  /** Identifikation der Settings */
  public id: number;
  /** Standort zum SOT */
  public location: number;
  /** Währungssymbol */
  public currencySymbol: string;
  /** Standard-Mehrwertsteuersatz */
  public mwst1: number;
  /** ermäßigter Mehrwertsteuersatz */
  public mwst2: number;
  /** Ermöglicht eine Im Haus Bestellung */
  public allow_in_house: boolean;
  /** Im Haus Sortiment */
  public in_house_sortiment: string;
  /** Im Haus Preisebene */
  public in_house_default_pe: number;
  /** Ermöglicht zum Mitnehmen zu Bestellen */
  public allow_pickup: boolean;
  /** Sortiment für Außer Haus */
  public pickup_sortiment: string;
  /** Preisebene für Außer Haus */
  public pickup_default_pe: number;
  /** Zeige den CO2 Wert an */
  public use_co2: boolean;
  /** Zeige die Zusatzstoffe an */
  public use_additives: boolean;
  /** Zeige den BF-Modus an */
  public use_bf_mode: boolean;
  /** Zeige die Allergene an */
  public use_allergens: boolean;
  /** Zeige die Nährwerte an */
  public use_nutrition_values: boolean;
  /** Zeige die eCommerce Informationen an */
  public use_eCommerce: boolean = true;
  /** Abfragen als mit Bildern darstellen */
  public extra_as_image: boolean;
  /** MenuSettings_SortType */
  public sort_type: number;
  /** Mögliche Sprachen zur Auswahl */
  public languages: string[];
  /** Bezeichnungen der möglichen Sprachen, Index entspricht dem index in languages */
  public languages_name: string[];
  /** Initiale Sprache */
  public default_language: string;
  /** Inactivitäts Timeout bis zum öffnen des Inactivitätsdialogs */
  public inactive_dlg_show_timeout: number;
  /** Inactivitäts Timeout bis zums schließen des Inactivitätsdialogs */
  public inactive_dlg_close_timeout: number;
  /** Artikelverfügbarkeit anwenden */
  public use_product_availability: boolean;
  /** globaler nidrigster schwellwert für Artikelverfügbarkeit */
  public min_product_availability: number;
  /** Logo Änderungsdatum-Tag */
  public last_logo_image_update_at_ymd: number;
  /** Logo Änderungsdatum-Uhrzeit */
  public last_logo_image_update_at_hms: number;
  /** Logo Änderungsdatum */
  public get last_logo_image_update_at(): Moment {
    return Utils.GetMomentFromInt(this.last_logo_image_update_at_ymd, this.last_logo_image_update_at_hms);
  }
  /** Pfand anzeigen */
  public deposit_view: boolean;
  /** Zahlungsarten Bild Änderungsdatum-Tag */
  public last_payment_image_update_at_ymd: number;
  /** Zahlungsarten Bild Änderungsdatum-Uhrzeit */
  public last_payment_image_update_at_hms: number;
  /** Zahlungsarten Bild Änderungsdatum */
  public get last_payment_image_update_at(): Moment {
    return Utils.GetMomentFromInt(this.last_payment_image_update_at_ymd, this.last_payment_image_update_at_hms);
  }
  /** Nach Trinkgeld fragen */
  public request_tip: boolean;
  /** Verwendung von Pagern */
  public use_pager: boolean;
  /** Coupons verwenden */
  public use_coupons: boolean;
  /** Coupon Code länge */
  public coupons_length: number;
  /** Coupons scannen */
  public scan_coupons: boolean;
  /** Benefits am SOT einlösen */
  public allow_benefits_at_sot: boolean;
  /** Überspringen der Bestelltypabfrage, wenn es nur ein Bestelltyp gibt */
  public skip_ordertype_if_no_choise: boolean;
  /** Änderungsdatum-Tag */
  public change_at_ymd: number;
  /** Änderungsdatum-Uhrzeit */
  public change_at_hms: number;
  /** Änderungsdatum */
  public get change_at(): Moment {
    return Utils.GetMomentFromInt(this.change_at_ymd, this.change_at_hms);
  }
  /** Warengruppen nur als Bilder verwenden */
  public use_itemgroup_as_image: boolean;
  /** Display option for time-controlled articles (hide vs. show-with-hint) */
  public article_option: ArticleOptions;
}

export class SOTSettingsSyncDataModel extends SyncDataModel {
  public settings: SOTSettingsDataModel[];
  public get models(): DataModel[] { return this.settings; }
  public DeserializeType(): void {
    this._DeserializeType(SOTSettingsDataModel);
  }
}
